import { App, Button, Input, Modal, Select, Space, Tag, Typography } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { getBulkBillEmailPreview, sendBulkBillEmail, type BulkBillEmailPreviewGroup } from "../../api/entryApi";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { getApiErrorMessage } from "../../shared/utils/apiError";

type EmailOverrideRow = BulkBillEmailPreviewGroup & {
  key: string;
  recipients: string[];
  subject: string;
  bodyHtml: string;
};

export function BulkBillEmailModal({ billIds, open, onClose }: { billIds: number[]; open: boolean; onClose: () => void }) {
  const { message } = App.useApp();
  const [selectedPartyKey, setSelectedPartyKey] = useState<string | null>(null);
  const [rows, setRows] = useState<EmailOverrideRow[]>([]);

  const preview = useQuery({
    queryKey: ["bulk-bill-email-preview", billIds],
    enabled: open && billIds.length > 0,
    queryFn: () => getBulkBillEmailPreview(billIds)
  });

  useEffect(() => {
    if (!preview.data?.groups) return;
    const nextRows = preview.data.groups.map((group, index) => ({
      ...group,
      key: String(group.partyId ?? `party-${index}`),
      recipients: splitRecipients(group.to),
      subject: group.subject ?? "",
      bodyHtml: group.bodyHtml ?? ""
    }));
    setRows(nextRows);
    setSelectedPartyKey(nextRows[0]?.key ?? null);
  }, [preview.data]);

  const selectedGroup = useMemo(() => rows.find((row) => row.key === selectedPartyKey) ?? rows[0], [rows, selectedPartyKey]);
  const sendMutation = useMutation({
    mutationFn: async () => {
      return sendBulkBillEmail({
        billIds,
        emailOverrides: rows
          .filter((row) => row.partyId && row.recipients.length)
          .map((row) => ({
            partyId: Number(row.partyId),
            email: row.recipients.join(", "),
            subject: row.subject,
            bodyHtml: row.bodyHtml
          }))
      });
    },
    onSuccess: (data: any) => {
      message.success(data?.message ?? "Bulk email sent");
      onClose();
    },
    onError: (err) => message.error(getApiErrorMessage(err, "Unable to send bulk email"))
  });

  const updateRow = (key: string, patch: Partial<EmailOverrideRow>) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...patch } : row));
  };
  const hasInvalidRecipients = rows.some((row) => row.recipients.some((email) => !isEmail(email)));

  return (
    <Modal
      title={`Bulk Email - ${billIds.length} selected invoice(s)`}
      open={open}
      width={1120}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button key="send" type="primary" loading={sendMutation.isPending} disabled={!rows.some((row) => row.recipients.length) || hasInvalidRecipients} onClick={() => sendMutation.mutate()}>
          Send Emails
        </Button>
      ]}
    >
      <Space direction="vertical" size={12} className="bulk-email-modal">
        <Typography.Text type="secondary">
          From {preview.data?.fromName || "configured sender"} {preview.data?.fromEmail ? `(${preview.data.fromEmail})` : ""}
        </Typography.Text>
        <Typography.Text type="secondary">Each party receives one message containing the selected bills and their PDF attachments.</Typography.Text>
        <PersistedTable<EmailOverrideRow>
          tableId="bulk-bill-email"
          rowKey="key"
          size="small"
          bordered
          loading={preview.isLoading}
          dataSource={rows}
          pagination={false}
          rowClassName={(row) => row.key === selectedPartyKey ? "selected-email-party" : ""}
          onRow={(row) => ({ onClick: () => setSelectedPartyKey(row.key) })}
          columns={[
            {
              title: "Party",
              render: (_, row) => (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{row.partyName ?? "Unknown"}</Typography.Text>
                  <Typography.Text type="secondary">{row.billCount ?? row.billIds?.length ?? 0} invoice(s): {(row.billNos ?? []).join(", ")}</Typography.Text>
                </Space>
              )
            },
            {
              title: "To",
              width: 390,
              render: (_, row) => (
                <Select
                  mode="tags"
                  value={row.recipients}
                  tokenSeparators={[",", ";"]}
                  placeholder="Type emails separated by commas"
                  status={row.recipients.some((email) => !isEmail(email)) ? "error" : undefined}
                  onChange={(recipients) => updateRow(row.key, { recipients: recipients.map((email) => email.trim()).filter(Boolean) })}
                  style={{ width: "100%" }}
                />
              )
            },
            { title: "Status", width: 120, render: (_, row) => row.recipients.length ? <Tag color="green">Sendable</Tag> : <Tag color="gold">No Email</Tag> }
          ]}
        />
        <div className="bulk-email-preview">
          {selectedGroup ? (
            <>
              <label className="bulk-email-field-label" htmlFor="bulk-email-subject">Subject</label>
              <Input
                id="bulk-email-subject"
                value={selectedGroup.subject}
                onChange={(event) => updateRow(selectedGroup.key, { subject: event.target.value })}
              />
              <div className="bulk-email-field-label">Message</div>
              <div
                key={selectedGroup.key}
                className="bulk-email-body-editor"
                contentEditable
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: selectedGroup.bodyHtml }}
                onInput={(event) => updateRow(selectedGroup.key, { bodyHtml: event.currentTarget.innerHTML })}
              />
              <Typography.Text type="secondary">
                Attachments: {(selectedGroup.billNos ?? []).map((billNo) => `Invoice_${billNo}.pdf`).join(", ") || "PDF invoices will be generated when sent"}
              </Typography.Text>
            </>
          ) : (
            <Typography.Text type="secondary">Select a party to compose its email.</Typography.Text>
          )}
        </div>
      </Space>
    </Modal>
  );
}

function splitRecipients(value?: string | null) {
  return String(value ?? "").split(/[;,]/).map((email) => email.trim()).filter(Boolean);
}

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
